import * as Yup from "yup";
import {
  gstRegExp,
  passRegrex,
  passwordError,
  phoneRegExp,
} from "./validation";
import { USER_ROLE } from "./userType";
import {
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_MB,
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
} from "./constants";
const userRoleSchema = Yup.string()
  .oneOf([
    USER_ROLE.COORDINATOR,
    USER_ROLE.MERCHANT,
    USER_ROLE.SERVICE_PARTNER,
    USER_ROLE.CUSTOMER,
  ])
  .required();

export const LoginSchema = () =>
  Yup.object().shape({
    phoneNumber: Yup.string()
      .trim()
      .required("Enter the required field")
      .matches(phoneRegExp, "Please enter valid Mobile Number")
      .min(10, "Mobile Number must be 10 digit number"),
    password: Yup.string()
      .trim()
      .required("Enter the required field")
      .max(16, "Password should be maximum of 16 characters")
      .min(8, "Password should be minimum of 8 characters")
      .matches(passRegrex, passwordError),
  });

export const ForgotPasswordSchema = () =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Enter the required field")
      .email("Please enter a valid email address"),
  });

export const OtpSchema = () => {
  return Yup.object().shape({
    otp: Yup.string()
      .trim()
      .required("Please fill in the required field")
      .matches(/^[0-9]/, "OTP must be a number")
      .max(6, "OTP must be 6 digit number")
      .min(6, "OTP must be 6 digit number"),
  });
};

export const ResetPasswordSchema = () =>
  Yup.object().shape({
    NewPassword: Yup.string()
      .trim()
      .required("Password is required")
      .matches(passRegrex, passwordError)
      .max(16, "Password should be maximum of 16 characters")
      .min(8, "Password should be minimum of 8 characters"),
    ConfirmPassword: Yup.string()
      .trim()
      .required("Password is required")
      .matches(passRegrex, passwordError)
      .max(16, "Password should be maximum of 16 characters")
      .min(8, "Password should be minimum of 8 characters")
      .oneOf(
        [Yup.ref("NewPassword")],
        "New and Confirm Password should be same."
      ),
  });

export const ChangePasswordSchema = () =>
  Yup.object().shape({
    oldPassword: Yup.string().trim().required("Old Password is required"),
    newPassword: Yup.string()
      .trim()
      .required("New Password is required")
      .matches(passRegrex, passwordError)
      .max(16, "Password should be maximum of 16 characters")
      .min(8, "Password should be minimum of 8 characters"),
    confirmPassword: Yup.string()
      .trim()
      .required("Confirm Password is required ")
      .matches(passRegrex, passwordError)
      .max(16, "Password should be maximum of 16 characters")
      .min(8, "Password should be minimum of 8 characters")
      .oneOf(
        [Yup.ref("newPassword")],
        "New and Confirm Password should be same."
      ),
  });

export const createUserSchema = Yup.object().shape({
  role: userRoleSchema,
  firstName: Yup.string()
    .min(2, "First Name must be at least 2 characters")
    .max(30, "First Name must be at most 30 characters")
    .required("Enter the required field"),
  lastName: Yup.string().optional()
    .min(2, "Last Name must be at least 2 characters")
    .max(30, "Last Name must be at most 30 characters"),
  email: Yup.string()
    .trim()
    .required("Enter the required field")
    .email("Please enter a valid email address"),
  phoneNumber: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
    .required("Enter the required field"),
  profilePhoto: Yup.string().optional(),
  password: Yup.string()
    .matches(
      passRegrex,
      "Must be at least 8 characters long, and contain at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character (@ $ ! % * ? &)"
    )
  ,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), ""], "Passwords must match")
    .required("Enter the required field"),

  addressLine1: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER || role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(200, "Address Line1 name must be less than 32 characters long")
        .min(2, "Address Line1 name must be 2 characters long");
    return schema
      .optional()
      .max(200, "Address Line1 name must be less than 32 characters long")
      .min(2, "Address Line1 name must be 2 characters long");
  }),
  addressLine2: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER || role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(200, "Address Line2 name must be less than 32 characters long")
        .min(2, "Address Line2 name must be 2 characters long");
    return schema
      .optional()
      .max(200, "Address Line2 name must be less than 32 characters long")
      .min(2, "Address Line2 name must be 2 characters long");
  }),
  city: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER || role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(32, "City name must be less than 32 characters long")
        .min(2, "City name must be 2 characters long");
    else return schema
      .optional()
      .max(32, "City name must be less than 32 characters long")
      .min(2, "City name must be 2 characters long");
  }),
  state: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER || role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(32, "State name must be less than 32 characters long")
        .min(2, "State name must be 2 characters long");
    else return schema
      .optional()
      .max(32, "State name must be less than 32 characters long")
      .min(2, "State name must be 2 characters long");
  }),
  pincode: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER || role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .length(6, "Pin code must be 6 digit number");
    else return schema
      .optional().length(6, "Pin code must be 6 digit number");
  }),
  gstNumber: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT)
      return schema
        .optional()
        .matches(gstRegExp, "Please enter correct GSTIN")
        .max(16);
    return schema;
  }),
  document: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.COORDINATOR || role[0] === USER_ROLE.SERVICE_PARTNER)
      return schema.required("Enter the required field");
    return schema;
  }),
  salary: Yup.number().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.COORDINATOR || role[0] === USER_ROLE.SERVICE_PARTNER)
      return schema
        .required("Enter the required field")
        .min(1, "Salary should be minimum 1 ");
    return schema;
  }),
  dateOfJoining: Yup.date().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.COORDINATOR || role[0] === USER_ROLE.MERCHANT || role[0] === USER_ROLE.SERVICE_PARTNER)
      return schema.required("Enter the required field");
    return schema;
  }),
  registrationNumber: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT) return schema.optional();
    return schema;
  }),
  franchiseAmount: Yup.number().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT) return schema.required("Enter the required field");
    return schema;
  }),
  numberOfStaff: Yup.number().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT)
      return schema
        .required("Enter the required field")
        .min(1, "Number of Service Partners should be minimum 1");
    return schema;
  }),
  vendorId: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.SERVICE_PARTNER) return schema.required("Enter the required field");
    return schema;
  }),
});

export const editUserSchema = Yup.object().shape({
  role: userRoleSchema,
  firstName: Yup.string()
    .min(2, "First Name must be at least 2 characters")
    .max(30, "First Name must be at most 30 characters")
    .required("Enter the required field"),
  lastName: Yup.string()
    .transform((_, original) => (original ? original : undefined))
    .min(2, "Last Name must be at least 2 characters")
    .max(30, "Last Name must be at most 30 characters")
    .optional(),
  // Customers can be registered with a phone number only
  email: Yup.string()
    .trim()
    .optional()
    .email("Please enter a valid email address"),
  phoneNumber: Yup.string()
    .matches(/^\+?[1-9]\d{7,14}$/, "Must be a valid phone number")
    .required("Enter the required field"),
  profilePhoto: Yup.string().optional(),
  // Blank password means "keep the current password"
  password: Yup.string()
    .transform((_, original) =>
      typeof original === "string" && original.trim() ? original : undefined
    )
    .matches(
      passRegrex,
      "Must be at least 8 characters long, and contain at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character (@ $ ! % * ? &)"
    )
    .optional(),
  confirmPassword: Yup.string()
    .transform((_, original) =>
      typeof original === "string" && original.trim() ? original : undefined
    )
    .when("password", (password, schema) => {
      const nextPassword = Array.isArray(password) ? password[0] : password;
      if (nextPassword) {
        return schema
          .required("Enter the required field")
          .oneOf([Yup.ref("password")], "Passwords must match");
      }
      return schema.optional();
    }),
  // Only the fields the role actually sends to update-user are required
  addressLine1: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(200, "Address Line1 name must be less than 32 characters long")
        .min(2, "Address Line1 name must be 2 characters long");
    return schema
      .optional()
      .max(200, "Address Line1 name must be less than 32 characters long");
  }),
  addressLine2: Yup.string().when("role", (role, schema) => {
    if (
      role[0] === USER_ROLE.CUSTOMER ||
      role[0] === USER_ROLE.MERCHANT ||
      role[0] === USER_ROLE.SERVICE_PARTNER ||
      role[0] === USER_ROLE.COORDINATOR
    )
      return schema
        .required("Enter the required field")
        .max(200, "Address Line2 name must be less than 32 characters long")
        .min(2, "Address Line2 name must be 2 characters long");
    return schema
      .optional()
      .max(200, "Address Line2 name must be less than 32 characters long");
  }),
  city: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.CUSTOMER || role[0] === USER_ROLE.MERCHANT)
      return schema
        .required("Enter the required field")
        .max(32, "City name must be less than 32 characters long")
        .min(2, "City name must be 2 characters long");
    else return schema
      .optional()
      .max(32, "City name must be less than 32 characters long");
  }),
  state: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .max(32, "State name must be less than 32 characters long")
        .min(2, "State name must be 2 characters long");
    else return schema
      .optional()
      .max(32, "State name must be less than 32 characters long");
  }),
  pincode: Yup.string().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.CUSTOMER)
      return schema
        .required("Enter the required field")
        .length(6, "Pin code must be 6 digit number");
    else return schema.optional();
  }),
  gstNumber: Yup.string()
    .transform((_, original) => (original ? original : undefined))
    .when("role", (role, schema) => {
      if (role[0] === USER_ROLE.MERCHANT)
        return schema
          .optional()
          .matches(gstRegExp, "Please enter correct GSTIN")
          .max(16);
      return schema.optional();
    }),
  document: Yup.string().optional(),
  salary: Yup.number()
    .transform((value, original) =>
      original === "" || original === null || original === undefined
        ? undefined
        : value
    )
    .nullable()
    .when("role", (role, schema) => {
      if (role[0] === USER_ROLE.SERVICE_PARTNER)
        return schema
          .required("Enter the required field")
          .min(1, "Salary should be minimum 1 ");
      return schema.optional();
    }),
  dateOfJoining: Yup.date()
    .transform((value, original) =>
      original === "" || original == null ? undefined : value
    )
    .nullable()
    .when("role", (role, schema) => {
      if (role[0] === USER_ROLE.SERVICE_PARTNER)
        return schema.required("Enter the required field");
      return schema.optional();
    }),
  registrationNumber: Yup.string().optional(),
  franchiseAmount: Yup.number()
    .transform((value, original) =>
      original === "" || original === null || original === undefined
        ? undefined
        : value
    )
    .nullable()
    .when("role", (role, schema) => {
      if (role[0] === USER_ROLE.MERCHANT)
        return schema.required("Enter the required field");
      return schema.optional();
    }),
  numberOfStaff: Yup.number().when("role", (role, schema) => {
    if (role[0] === USER_ROLE.MERCHANT)
      return schema
        .required("Enter the required field")
        .min(1, "Number of Service Partners should be minimum 1");
    return schema;
  }),
  vendorId: Yup.string().optional(),
});

export const createOrderSchema = Yup.object().shape({
  userId: Yup.string().required("Please select user"),
  franchiseId: Yup.string().required("Please select franchise"),
  paymentMode: Yup.string().required("Please select payment mode"),
  serviceCategory: Yup.string().required("Please select service category"),
  serviceId: Yup.string().required("Please select service"),
  serviceDate: Yup.string().required("Please select service date"),
  serviceTime: Yup.string().required("Please select time of service"),
  vechileId: Yup.string().when("serviceCategory", {
    is: (category: string) => {
      const slug = `${category || ""}`.toLowerCase();
      return slug === "car" || slug === "bike";
    },
    then: (schema) => schema.required("Please select user vehicle"),
    otherwise: (schema) => schema.notRequired(),
  }),
  vehicleRegNo: Yup.string().when("serviceCategory", {
    is: (category: string) => {
      const slug = `${category || ""}`.toLowerCase();
      return slug === "car" || slug === "bike";
    },
    then: (schema) =>
      schema.required("Please enter vehicle registration number"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine1: Yup.string().required("Please enter address line 1"),
  city: Yup.string().required("Please enter city name"),
  state: Yup.string().required("Please enter state name"),
  pincode: Yup.string().max(6).required("Please enter pincode"),
  addressType: Yup.string().when("addressId", {
    is: (addressId: string) => !`${addressId || ""}`.trim(),
    then: (schema) => schema.required("Please select address type"),
    otherwise: (schema) => schema.notRequired(),
  }),
  discountValue: Yup.number().min(0, "Discount cannot be negative").nullable(),
  couponCode: Yup.string().trim().nullable(),
});

export const CarTypesSchema = () => {
  return Yup.object().shape({
    carType: Yup.string()
      .trim()
      .required("Please enter the required field")
      .min(3, "Car Type should be minimum of 3 characters")
      .max(15, "Car Type should be maximum of 15 characters"),
  });
};

export const CarCompanySchema = () => {
  return Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Please enter the required field")
      .min(3, "Company name should be minimum of 3 characters")
      .max(15, "Company name should be maximum of 15 characters"),
    logo: Yup.string().optional(),
  });
};
export const DiscountSchema = () => {
  return Yup.object().shape({
    // serviceDate: Yup.string().required("Enter the required field"),
    // status:  Yup.string().required("Enter the required field"),
    endTime: Yup.string().required("Enter the endTime field"),
    startTime: Yup.string().required("Enter the startTime field"),
    discountValue: Yup.number().required("Enter the Discount field"),
  });
};

export const ServiceTypesSchema = () => {
  return Yup.object().shape({
    category: Yup.number()
      .oneOf([0, 1, 2, 3, 4], "Category is invalid")
      .required("Category is required"),

    title: Yup.string()
      .trim()
      .min(3, "Service name should be minimum of 3 characters")
      .max(150, "Service name should be maximum of 150 characters")
      .required("Service name is required"),

    serviceTime: Yup.string().trim().required("Service time is required"),

    imageKey: Yup.string(),

    requirementFromUser: Yup.array().of(
      Yup.object().shape({
        _id: Yup.string().optional(),
        title: Yup.string()
          .trim()
          .max(100)
          .required("Requirement title is required"),
        image: Yup.string().optional(),
        previewUrl: Yup.string().optional(),
        file: Yup.mixed().optional(),
      })
    ),

    tools: Yup.array().of(
      Yup.object().shape({
        _id: Yup.string().optional(),
        title: Yup.string().trim().max(100).required("Tool title is required"),
        image: Yup.string().optional(),
        previewUrl: Yup.string().optional(),
        file: Yup.mixed().optional(),
      })
    ),

    howItWorks: Yup.array()
      .of(
        Yup.object().shape({
          _id: Yup.string().optional(),
          title: Yup.string()
            .trim()
            .max(150)
            .required("How it works title is required"),
          description: Yup.string().trim().max(500),
        })
      )
      .min(1, "Add at least one how it works step"),

    benefits: Yup.array()
      .of(
        Yup.object().shape({
          _id: Yup.string().optional(),
          title: Yup.string().trim().max(150).required("Benefit title is required"),
          description: Yup.string().trim().max(500),
        })
      )
      .min(1, "Add at least one benefit"),
  });
};

/** Same rules as create, keyed to GET/PATCH /services/service-detail fields. */
export const ServiceDetailSchema = () =>
  ServiceTypesSchema()
    .omit(["title", "imageKey"])
    .shape({
      serviceName: Yup.string()
        .trim()
        .min(3, "Service name should be minimum of 3 characters")
        .max(150, "Service name should be maximum of 150 characters")
        .required("Service name is required"),
      image: Yup.string(),
      _id: Yup.string().optional(),
    });

export const AddReviewSchema = () => {
  return Yup.object().shape({
    text: Yup.string()
      .trim()
      .max(500, "Description should be maximum of 500 characters"),
  });
};

export const UserBlockedSchema = () => {
  return Yup.object().shape({
    reason: Yup.string().required("Please select a reason"),
    text: Yup.string().when("reason", (reason, schema) => {
      if (reason[0] === "Others")
        return schema.required("Enter the required field");
      return schema;
    }),
  });
};

export const PushNotificationSchema = () => {
  return Yup.object().shape({
    description: Yup.string()
      .trim()
      .required("Please enter the required field")
      .max(500, "Description should be maximum of 500 characters"),
    title: Yup.string()
      .trim()
      .required("Please enter the required field")
      .max(120, "Title should be maximum of 120 characters"),
    recipientType: Yup.string().required("Please select recipient"),
  });
};

export const SmsNotificationSchema = () => {
  return Yup.object().shape({
    description: Yup.string()
      .trim()
      .required("Please enter the required field")
      .max(500, "Description should be maximum of 500 characters"),
    recipientType: Yup.string().required("Please select recipient"),
  });
};

export const EmailNotificationSchema = () => {
  return Yup.object().shape({
    description: Yup.string()
      .trim()
      .required("Please enter the required field")
      .max(500, "Description should be maximum of 500 characters"),
    tagline: Yup.string()
      .trim()
      .required("Please enter the required field")
      .max(120, "Title should be maximum of 100 characters"),
    recipientType: Yup.string().required("Please select recipient"),
  });
};

export const TestimonialSchema = (isEdit = false) =>
  Yup.object().shape({
    category: Yup.number()
      .oneOf([0, 1, 2, 3, 4], "Category is invalid")
      .required("Category is required"),
    title: Yup.string().trim().max(120, "Thumbnail must be under 120 characters"),
    thumbnailFile: Yup.mixed()
      .nullable()
      .test(
        "required-if-no-existing-thumbnail",
        "Thumbnail image is required",
        function (file) {
          if (file) return true;
          if (isEdit && this.parent.existingThumbnailUrl?.trim()) return true;
          return false;
        }
      )
      .test(
        "fileType",
        "Please upload a valid image file (JPG, PNG, WebP, AVIF).",
        (file) =>
          !file || ACCEPTED_IMAGE_TYPES.includes((file as File).type)
      )
      .test(
        "fileSize",
        `Thumbnail must be smaller than ${MAX_IMAGE_SIZE_MB}MB.`,
        (file) =>
          !file || (file as File).size <= MAX_IMAGE_SIZE_BYTES
      ),
    existingThumbnailUrl: Yup.string().nullable(),
    mediaFile: Yup.mixed()
      .nullable()
      .test("required-if-no-existing-media", "Video is required", function (file) {
        if (file) return true;
        if (isEdit && this.parent.existingMediaUrl?.trim()) return true;
        return false;
      })
      .test(
        "fileType",
        "Please upload a valid video file (MP4, MPEG).",
        (file) =>
          !file ||
          ACCEPTED_VIDEO_TYPES.includes((file as File).type)
      )
      .test(
        "fileSize",
        `Video must be smaller than ${MAX_VIDEO_SIZE_MB}MB.`,
        (file) =>
          !file || (file as File).size <= MAX_VIDEO_SIZE_BYTES
      ),
    existingMediaUrl: Yup.string().nullable(),
  });

export const editOrderSchema = Yup.object().shape({
  merchant: Yup.object().required("Please select franchise"),
  serviceDate: Yup.string().required("Please select service date"),
  serviceTime: Yup.string().required("Please select time of service"),
  vehicleRegNo: Yup.string().required(
    "Please enter vehicle registration number"
  ),
  addressLine1: Yup.string().required("Please enter address line 1"),
  city: Yup.string().required("Please enter city name"),
  state: Yup.string().required("Please enter state name"),
  pincode: Yup.string().max(6).required("Please enter pincode"),
});


export const editMerchantOnlySchema = Yup.object().shape({
  franchiseId: Yup.string().required("Please select franchise"),
});

export const editOrderDetailsSchema = Yup.object().shape({
  franchiseId: Yup.string().required("Please select franchise"),
  vehicleRegNo: Yup.string(),
  addressLine1: Yup.string().required("Please enter address line 1"),
  city: Yup.string().required("Please enter city name"),
  state: Yup.string().required("Please enter state name"),
  pincode: Yup.string().max(6).required("Please enter pincode"),
  addressType: Yup.string().required("Please select address type"),
  serviceDate: Yup.string().required("Please select service date"),
  serviceTime: Yup.string().required("Please select time of service"),
});

const optionalNumberSchema = (message: string) =>
  Yup.number().transform((value, originalValue) =>
    originalValue === "" || originalValue === null || originalValue === undefined
      ? undefined
      : value
  ).typeError(message);

export const createCouponSchema = Yup.object().shape({
  couponCode: Yup.string()
    .trim()
    .min(2, "Coupon Code must be at least 2 characters")
    .max(40, "Coupon Code must be at most 40 characters")
    .required("Enter the required field"),
  title: Yup.string()
    .trim()
    .max(50, "Title must be at most 50 characters")
    .optional(),
  description: Yup.string()
    .trim()
    .max(100, "Description must be at most 100 characters")
    .optional(),
  image: Yup.string().optional(),
  categories: Yup.array()
    .of(Yup.number())
    .min(1, "Please select at least one category")
    .required("Enter the required field"),
  isPercentage: Yup.boolean().required("Please select discount type"),
  isDefault: Yup.boolean().required("Enter the required field"),
  couponValue: optionalNumberSchema("Enter a valid coupon value")
    .moreThan(0, "Coupon value must be greater than 0")
    .required("Enter the required field")
    .when("isPercentage", {
      is: true,
      then: (schema) =>
        schema.max(100, "Percentage value cannot be more than 100"),
    }),
  couponLevelRedemption: optionalNumberSchema(
    "Enter a valid coupon level redemption"
  )
    .integer("Coupon level redemption must be a whole number")
    .min(1, "Coupon level redemption must be at least 1")
    .optional(),
  userLevelRedemption: optionalNumberSchema(
    "Enter a valid user level redemption"
  )
    .integer("User level redemption must be a whole number")
    .min(1, "User level redemption must be at least 1")
    .optional(),
  startTime: Yup.mixed()
    .required("Enter the required field")
    .test(
      "future-start",
      "Start time must be later than the current time",
      function (value) {
        if (!value) return true;
        const startMs = new Date(String(value)).getTime();
        if (!Number.isFinite(startMs)) return false;

        // Edit mode: keep the original startTime even if it is already in the past.
        const original = this.parent?._originalStartTime;
        if (original) {
          const originalMs = new Date(String(original)).getTime();
          // Allow unchanged original value (ignore sub-second serialization diffs).
          if (
            Number.isFinite(originalMs) &&
            Math.abs(originalMs - startMs) < 1000
          ) {
            return true;
          }
        }

        return startMs > Date.now();
      }
    ),
  endTime: Yup.mixed()
    .required("Enter the required field")
    .test(
      "after-start",
      "End time must be greater than start time",
      function (value) {
        const { startTime } = this.parent;
        if (!startTime || !value) return true;
        return (
          new Date(String(value)).getTime() > new Date(String(startTime)).getTime()
        );
      }
    ),
});